<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query';
import { ref, computed } from 'vue';
import { groupsApi, type Group } from '@/api/groups';
import { patientsApi } from '@/api/patients';
import { usePermissions } from '@/composables/usePermissions';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import GroupForm from './group-form.vue';
import GroupDiscardForm from './group-discard-form.vue';

const { hasPermission } = usePermissions();
const { tableProps } = useResponsiveTable();

const selectedGroup = ref<Group | null>(null);
const showForm = ref(false);
const hasActions = hasPermission.value('groups', 'manage');

const { data: groups, refetch: refetchGroups, isPending: isLoading } = useQuery({
  queryKey: ['groups'],
  queryFn: () => groupsApi.index(),
  initialData: [],
});

const { data: allPatients } = useQuery({
  queryKey: ['patients', 'forFilters', 'orphanCount'],
  queryFn: () => patientsApi.indexForFilters(),
  initialData: [],
});

const groupedPatientIds = computed(() => {
  const set = new Set<number>();
  for (const group of groups.value) {
    for (const id of group.patientIds) set.add(id);
  }
  return set;
});

const orphanPatientCount = computed(() =>
  allPatients.value.filter(p => !groupedPatientIds.value.has(p.id)).length,
);

const actionSuccess = ref(false);

function openCreateForm() {
  selectedGroup.value = null;
  showForm.value = true;
}

function openEditForm(group: Group) {
  selectedGroup.value = group;
  showForm.value = true;
}

function handleSuccess() {
  refetchGroups();
  actionSuccess.value = true;
}

function handleFormClose() {
  showForm.value = false;
  selectedGroup.value = null;
}

const headers = computed(() => {
  const base: { title: string, key: string, sortable?: boolean }[] = [
    { title: 'Nombre', key: 'name' },
    { title: 'Pacientes', key: 'patientsCount' },
    { title: 'Profesionales', key: 'caregiversCount' },
    { title: 'Usuarios', key: 'usersCount' },
    { title: 'Creado', key: 'humanCreatedAt' },
  ];
  if (hasActions) base.push({ title: 'Acciones', key: 'actions', sortable: false });
  return base;
});
</script>

<template>
  <div>
    <v-data-table
      v-bind="tableProps"
      :headers="headers"
      :header-props="{ class: 'font-weight-bold' }"
      :items="groups"
      :loading="isLoading"
      loading-text="Cargando grupos..."
    >
      <template #top>
        <v-alert
          v-if="orphanPatientCount > 0"
          type="warning"
          variant="tonal"
          class="mx-4 mb-4"
          density="compact"
        >
          {{ orphanPatientCount }} paciente(s) no están asignados a ningún grupo.
          Los usuarios con grupos asignados no podrán verlos hasta que sean incluidos en alguno.
        </v-alert>
        <v-toolbar
          color="white"
          class="mx-2 mb-4"
          flat
        >
          <v-spacer />
          <v-btn
            v-if="hasPermission('groups', 'manage')"
            class="mb-2 text-white border bg-primary"
            @click="openCreateForm"
          >
            <v-icon
              start
              icon="mdi-plus"
            />
            Agregar Grupo
          </v-btn>
        </v-toolbar>
      </template>

      <template #[`item.patientsCount`]="{ item }">
        <v-chip
          :color="item.patientsCount > 0 ? 'primary' : 'grey'"
          size="small"
        >
          {{ item.patientsCount }}
        </v-chip>
      </template>

      <template #[`item.caregiversCount`]="{ item }">
        <v-chip
          :color="item.caregiversCount > 0 ? 'primary' : 'grey'"
          size="small"
        >
          {{ item.caregiversCount }}
        </v-chip>
      </template>

      <template #[`item.usersCount`]="{ item }">
        <v-chip
          :color="item.usersCount > 0 ? 'primary' : 'grey'"
          size="small"
        >
          {{ item.usersCount }}
        </v-chip>
      </template>

      <template #[`item.actions`]="{ item }">
        <div
          v-if="hasActions"
          class="d-flex"
        >
          <v-btn
            v-if="hasPermission('groups', 'manage')"
            icon="mdi-pencil"
            size="small"
            variant="text"
            @click="openEditForm(item)"
          />
          <v-dialog
            max-width="600"
            scrollable
          >
            <template #activator="{ props: activatorProps }">
              <v-btn
                v-if="hasPermission('groups', 'manage')"
                v-bind="activatorProps"
                icon="mdi-archive"
                size="small"
                variant="text"
                color="warning"
              />
            </template>
            <template #default="{ isActive }">
              <group-discard-form
                :group="item"
                @close="isActive.value = false"
                @success="handleSuccess"
              />
            </template>
          </v-dialog>
        </div>
      </template>
    </v-data-table>

    <v-dialog
      v-model="showForm"
      max-width="800"
      scrollable
    >
      <group-form
        :group="selectedGroup"
        @close="handleFormClose"
        @success="handleSuccess"
      />
    </v-dialog>
  </div>

  <v-snackbar
    v-model="actionSuccess"
    :timeout="2000"
    color="success"
    rounded="pill"
  >
    <v-icon
      icon="mdi-check-circle"
      class="mr-4"
    />
    Cambios guardados exitosamente!
  </v-snackbar>
</template>
